'use client'

import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { ChevronDownIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useMemo } from 'react'

import { Button } from '@/components/ui/button'
import { SegmentedControl } from '@/components/ui/segmented-control'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { HedgeInstrumentField } from '@/features/hedge/components/hedge-instrument-field'
import { HedgePositionGroupPicker } from '@/features/hedge/components/hedge-position-group-picker'
import { HedgeUnderlyingSelect } from '@/features/hedge/components/hedge-underlying-select'
import { SingleHedgeExecutionFields } from '@/features/hedge/components/single-hedge-execution-fields'
import { validateSingleHedge } from '@/features/hedge/lib/build-single-hedge-params'
import type { HedgeScopeMode } from '@/features/hedge/types'
import { ExchangeAccountTabBar } from '@/features/order/order-form/components/widget/exchange-account-tab-bar'
import {
  FormMessage,
  FormSection,
  ORDER_FORM_FIELD_CLASS,
  ORDER_FORM_LABEL_CLASS,
} from '@/features/order/order-form/components/widget/order-form-fields'
import { useInstrumentMap } from '@/features/order/order-form/hooks/use-instrument-map'
import { useUnifiedOrderFormContext } from '@/features/order/order-form/lib/order-form-context'
import {
  positionsForAccount,
  scopedUnderlying,
  singleHedgeContext,
  singleHedgeTabForm,
  singleHedgeUnderlyingOptions,
} from '@/features/order/order-form/lib/single-hedge-tab'
import { useMultiExchangeInstrumentMap } from '@/hooks/use-multi-exchange-instrument-map'
import { useNumberInput } from '@/hooks/use-number-format'
import { parseExchangeField, toExchangeEnum, tradeVenueToExchangeKey } from '@/lib/proto/util'
import { accountDataLoadingAtom, allPositionsAtom } from '@/state/account'
import {
  DEFAULT_SINGLE_HEDGE_TAB_STATE,
  orderFormSelectionAtomFamily,
  OrderFormSummaryTab,
  orderFormSummaryTabAtomFamily,
  singleHedgeTabAtomFamily,
  type SingleHedgeTabState,
} from '@/state/order'
import { HedgeDeltaDenomination } from '@/types/generated/algo'

/**
 * The Hedge tab's body, in place of the order rows: what to hedge (the selected instrument's
 * underlying, picked positions, or the whole portfolio), the instrument it trades, the target
 * delta, and how it executes. The hedge dialog's own fields and validation are reused through
 * singleHedgeTabForm.
 */
export function SingleHedgeParams() {
  const t = useTranslations('orderForm')
  const th = useTranslations('trade.hedgeBuilder')
  const {
    cardId,
    fields,
    orderAccounts,
    setOrderAccountFilter,
    getSingleHedgeTarget,
    getSubmissionRows,
    primaryExchange,
  } = useUnifiedOrderFormContext()
  const [state, setState] = useAtom(singleHedgeTabAtomFamily(cardId))
  const setSelection = useSetAtom(orderFormSelectionAtomFamily(cardId))
  const setTab = useSetAtom(orderFormSummaryTabAtomFamily(cardId))
  const allPositions = useAtomValue(allPositionsAtom)
  const instrumentMap = useInstrumentMap(primaryExchange)
  const multiExchangeInstrumentMap = useMultiExchangeInstrumentMap()
  const targetInput = useNumberInput(state.target, { allowNegative: true })

  const target = getSingleHedgeTarget()
  const exchange = target?.exchange ?? primaryExchange
  const ctx = useMemo(
    () =>
      singleHedgeContext({
        selectedSymbol: target?.selectedSymbol,
        accountId: target?.account.accountId ?? '',
        exchange,
        allPositions,
        instrumentMap,
        multiExchangeInstrumentMap,
      }),
    [
      target?.selectedSymbol,
      target?.account.accountId,
      exchange,
      allPositions,
      instrumentMap,
      multiExchangeInstrumentMap,
    ]
  )
  const form = useMemo(() => singleHedgeTabForm(state, ctx), [state, ctx])
  const errors = useMemo(() => {
    const map: Record<string, string> = {}
    for (const e of validateSingleHedge(
      form,
      state,
      ctx.positions,
      ctx.instrumentMap,
      ctx.multiExchangeInstrumentMap
    )) {
      if (!map[e.field]) map[e.field] = e.message
    }
    return map
  }, [form, state, ctx])

  const exchangeAccounts = useMemo(
    () =>
      orderAccounts.filter(
        (a) => toExchangeEnum(tradeVenueToExchangeKey(a.tradeVenue)) === exchange
      ),
    [orderAccounts, exchange]
  )
  const positionSymbolOptions = useMemo(
    () => Array.from(new Set(ctx.positions.map((p) => p.symbol).filter(Boolean))).sort(),
    [ctx.positions]
  )
  const underlyingOptions = useMemo(() => singleHedgeUnderlyingOptions(ctx), [ctx])
  const allExchangeInstruments = useMemo(
    () => Array.from(multiExchangeInstrumentMap.values()).flatMap((m) => Array.from(m.values())),
    [multiExchangeInstrumentMap]
  )

  // Nothing open on this account means nothing to hedge in any scope: say so and offer this
  // venue's accounts that do hold positions, rather than seeding an underlying that can't run.
  // Unknown until positions have loaded, so the form doesn't flash the empty state first.
  const positionsLoading = useAtomValue(accountDataLoadingAtom).positions
  const hasNothingToHedge = Boolean(target) && !positionsLoading && ctx.positions.length === 0
  // The notice already says why; the fields' own "needs an open position" errors would repeat it.
  const fieldErrors: Record<string, string> = hasNothingToHedge ? {} : errors
  const accountsWithPositions = useMemo(
    () =>
      hasNothingToHedge
        ? exchangeAccounts
            .map((account) => ({
              account,
              count: positionsForAccount(allPositions, account.accountId).length,
            }))
            .filter(({ count }) => count > 0)
        : [],
    [hasNothingToHedge, exchangeAccounts, allPositions]
  )

  const update = (patch: Partial<SingleHedgeTabState>) =>
    setState((prev) => ({ ...prev, ...patch }))

  const underlyingLabel =
    state.scopeMode === 'underlying'
      ? scopedUnderlying(state, ctx) || th('underlying')
      : th('underlying')

  return (
    // Same frame as UnifiedOrderForm's body (p-4 pt-0, account bar bled to the edges) so
    // switching tabs doesn't shift the content.
    <div className="flex w-full flex-col gap-4 p-4 pt-0">
      {target && (
        <div className="-mx-4">
          <ExchangeAccountTabBar
            exchange={exchange}
            accounts={exchangeAccounts}
            activeAccountId={target.account.accountId}
            onSelectAccount={(accountId) =>
              setOrderAccountFilter(exchange, accountId, fields[0]?.accountId)
            }
            plainInactiveTabs
            onRemoveAll={() => {
              const accountRows = getSubmissionRows().filter(
                (row) =>
                  row.account.accountId === target.account.accountId && row.exchange === exchange
              )
              const matchesRow = (symbol: string, accountId?: string, rowExchange?: string) =>
                accountRows.some(
                  (row) =>
                    row.item.symbol === symbol &&
                    (row.item.accountId ?? '') === (accountId ?? '') &&
                    row.exchange === parseExchangeField(rowExchange, primaryExchange)
                )
              setSelection((prev) => ({
                ...prev,
                selectedOptions: prev.selectedOptions.filter(
                  (option) => !matchesRow(option.instrumentName, option.accountId, option.exchange)
                ),
                selectedInstruments: prev.selectedInstruments.filter(
                  (instrument) =>
                    !matchesRow(instrument.symbol, instrument.accountId, instrument.exchange)
                ),
              }))
              setState(DEFAULT_SINGLE_HEDGE_TAB_STATE)
              // An empty Single Hedge ticket resolves a fallback account, so return
              // to the normal ticket after removing this hedge's account.
              setTab(OrderFormSummaryTab.Limit)
            }}
            removeAllLabel={t('removeAccountOrderRows')}
          />
        </div>
      )}

      {hasNothingToHedge && target && (
        <div className="bg-foreground/5 flex flex-col gap-2.5 px-3 py-2.5">
          <div className="flex flex-col gap-0.5">
            <p className="text-foreground text-sm font-medium">
              {t('hedgeNoPositions', { account: target.account.name })}
            </p>
            <p className="text-foreground/50 text-xs">{t('hedgeNoPositionsHint')}</p>
          </div>
          {accountsWithPositions.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {accountsWithPositions.map(({ account, count }) => (
                <Button
                  key={account.accountId}
                  type="button"
                  variant="outline"
                  size="xs"
                  onClick={() =>
                    setOrderAccountFilter(exchange, account.accountId, fields[0]?.accountId)
                  }
                >
                  {t('hedgeOnAccount', { account: account.name, count })}
                </Button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* The form stays in view so the tab still reads as a hedge ticket; it just can't be
          filled in until the account has something open. */}
      <fieldset
        disabled={hasNothingToHedge}
        className="m-0 flex min-w-0 flex-col gap-4 border-0 p-0 transition-opacity disabled:opacity-40"
      >
        <div className="flex flex-col gap-2">
          <SegmentedControl<HedgeScopeMode>
            fill
            options={[
              { value: 'underlying', label: t('hedgeScopeUnderlying') },
              { value: 'positionGroup', label: t('hedgeScopePositions') },
              { value: 'portfolio', label: t('hedgeScopePortfolio') },
            ]}
            value={state.scopeMode}
            onChange={(scopeMode) => update({ scopeMode })}
          />
          {state.scopeMode === 'underlying' && (
            <HedgeUnderlyingSelect
              value={scopedUnderlying(state, ctx)}
              options={underlyingOptions.held}
              otherOptions={underlyingOptions.other}
              // A picked instrument belongs to the old underlying: fall back to the auto rule.
              onChange={(underlying) => update({ underlying, hedgeInstrument: null })}
              fieldClassName={ORDER_FORM_FIELD_CLASS}
            />
          )}
          {state.scopeMode === 'positionGroup' && (
            <HedgePositionGroupPicker
              selected={state.positionSymbols}
              positionSymbolOptions={positionSymbolOptions}
              error={fieldErrors.positionSymbols}
              onChange={(positionSymbols) => update({ positionSymbols })}
              instruments={allExchangeInstruments}
              fieldClassName={ORDER_FORM_FIELD_CLASS}
            />
          )}
          <FormMessage error={fieldErrors.underlyingAsset} />
        </div>

        <HedgeInstrumentField
          form={form}
          positions={ctx.positions}
          openUnderlyings={underlyingOptions.held}
          instrumentMap={ctx.instrumentMap}
          multiExchangeInstrumentMap={ctx.multiExchangeInstrumentMap}
          error={fieldErrors.hedgeInstrument}
          onChange={(patch) => {
            if (patch.hedgeInstrument !== undefined)
              update({ hedgeInstrument: patch.hedgeInstrument })
          }}
          className="w-full gap-2"
          labelClassName={ORDER_FORM_LABEL_CLASS}
          fieldClassName={ORDER_FORM_FIELD_CLASS}
        />

        {/* Target + unit as one joined field, like the limit price's anchor/value/unit. */}
        <FormSection label={t('hedgeTargetDelta')} error={fieldErrors['acceptable.target']}>
          <div className="border-foreground/10 bg-foreground/5 focus-within:border-foreground/40 flex h-9 border transition-colors">
            <input
              type="text"
              autoComplete="off"
              data-1p-ignore
              aria-label={t('hedgeTargetDelta')}
              placeholder="0"
              {...targetInput.bind((target) => update({ target }))}
              className="placeholder:text-foreground/30 text-foreground min-w-0 flex-1 bg-transparent px-3 text-right text-sm outline-none"
            />
            <span className="bg-foreground/10 w-px" />
            <Select
              value={String(state.denomination)}
              onValueChange={(v) => update({ denomination: Number(v) as HedgeDeltaDenomination })}
            >
              <SelectTrigger
                hideIcon
                aria-label={th('unit')}
                className="text-foreground/60 hover:bg-foreground/5 hover:text-foreground flex h-full w-auto min-w-20 shrink-0 cursor-pointer items-center justify-between gap-1 rounded-none border-none bg-transparent px-3 text-sm font-medium"
              >
                <SelectValue />
                <ChevronDownIcon className="size-3 shrink-0" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem
                  value={String(HedgeDeltaDenomination.HEDGE_DELTA_DENOMINATION_UNDERLYING_UNITS)}
                >
                  {underlyingLabel}
                </SelectItem>
                <SelectItem value={String(HedgeDeltaDenomination.HEDGE_DELTA_DENOMINATION_USD)}>
                  USD
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </FormSection>

        <SingleHedgeExecutionFields
          execution={state.execution}
          twapSeconds={state.twapSeconds}
          errors={fieldErrors}
          onChange={update}
        />
      </fieldset>
    </div>
  )
}
